import * as React from 'react';
import {connect} from 'react-redux';
import {Link as RoutedLink} from 'react-router-dom';

import classnames from 'classnames';

import {Line} from 'shells/divider/line';
import {Illustration} from 'shells/illustrations/illustration';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {MarketingBody} from 'shells/typography/marketing-body';
import {MarketingHeader} from 'shells/typography/marketing-header';

import type {DripSequenceFragment as DripSequence} from 'nutshell-graphql-types';

import {DripSequenceQuery} from '../../../email-marketing/graphql-wrappers/drip-sequence/drip-sequence-query';

import {clearActiveItem} from '../../ui-actions';

import {SalesAutomationEditorSidebar} from './sidebar/sales-automation-editor-sidebar';

import styles from './closed-lead-start-trigger-sidebar.module.css';

// @ts-expect-error
const mapDispatchToProps = function (dispatch): DispatchProps {
    return {
        onLinkClick: () => {
            dispatch(clearActiveItem());
        },
    };
};

type DispatchProps = {
    onLinkClick: () => void;
};

type OwnProps = DispatchProps & {
    entityId: string;
};

type ConnectedProps = OwnProps & {
    dripSequence?: DripSequence;
    isLoading: boolean;
};

class ClosedLeadStartTriggerSidebarComponent extends React.PureComponent<ConnectedProps> {
    render() {
        if (this.props.isLoading) {
            return (
                <SalesAutomationEditorSidebar
                    title='Loading this drip sequence…'
                    closeButton={false}
                    saveText='Done'
                >
                    <LoadingPage />
                </SalesAutomationEditorSidebar>
            );
        }

        const {dripSequence} = this.props;

        if (dripSequence) {
            const header = getHeader(dripSequence.status);
            const verb = getVerb(dripSequence.status);
            const description = getDescription(dripSequence.status);

            return (
                <SalesAutomationEditorSidebar title={header} closeButton={false} saveText='Done'>
                    <div className={classnames('pad-16', styles.container)}>
                        <StackLegacy spacing={12}>
                            <div className='light-text'>{description}</div>
                            <Line />
                            <div>{verb}:</div>
                            <Link
                                variant='primary'
                                // @ts-expect-error
                                as={RoutedLink}
                                to={dripSequence.htmlUrlPath}
                                onClick={() => {
                                    // close sidebar
                                    this.props.onLinkClick();
                                }}
                            >
                                {dripSequence.name}
                            </Link>
                        </StackLegacy>
                    </div>
                </SalesAutomationEditorSidebar>
            );
        }

        if (NutClientConfig.hasDripSequences) {
            return (
                <SalesAutomationEditorSidebar
                    title='Set up a drip sequence'
                    closeButton={false}
                    saveText='Done'
                >
                    <div className={classnames('pad-16', styles.container)}>
                        <StackLegacy spacing={12}>
                            <div className='light-text'>
                                Set up a drip sequence that will automatically begin sending when a
                                lead enters this stage of the pipeline.
                            </div>
                            <Line />
                            <div>
                                You can connect any drip sequence to this pipeline with these steps:
                            </div>
                            <div className='pad-8'>
                                <StackLegacy spacing={8}>
                                    <div>1. View any drip sequence</div>
                                    <div>
                                        2. Add a <b>start trigger</b>
                                    </div>
                                    <div>
                                        3. Select the <b>lead stage is entered</b> start trigger
                                    </div>
                                    <div>
                                        4. Select a <b>pipeline</b> and <b>stage</b>
                                    </div>
                                    <div>
                                        5. Make sure your drip sequence is <b>active</b>
                                    </div>
                                </StackLegacy>
                            </div>
                            <div>
                                After you follow these steps, come back here to see the drip
                                sequence built into your pipeline!
                            </div>
                            <Link
                                variant='primary'
                                // @ts-expect-error
                                as={RoutedLink}
                                to='/email-marketing/drip-sequences'
                                onClick={() => {
                                    // close sidebar
                                    this.props.onLinkClick();
                                }}
                            >
                                View your drip sequences
                            </Link>
                        </StackLegacy>
                    </div>
                </SalesAutomationEditorSidebar>
            );
        }

        return (
            <SalesAutomationEditorSidebar
                title='Try drip sequences'
                closeButton={false}
                saveText='Done'
            >
                <div className={styles['empty-state-container']}>
                    <div>
                        <MarketingHeader>
                            Automated emails at precisely the right moment
                        </MarketingHeader>
                        <MarketingBody>
                            Use drip sequences in Nutshell Campaigns to automatically send a series
                            of messages over time.
                        </MarketingBody>
                    </div>
                    <div style={{width: 200, textAlign: 'center'}}>
                        <Illustration src='drip-sequence-doodle' width='100%' height='100%' />
                    </div>
                    <Link
                        // @ts-expect-error
                        as={RoutedLink}
                        to='/email-marketing/drip-sequences'
                        newTab={true}
                        variant='button-primary'
                        size='xlarge'
                    >
                        Learn more
                    </Link>
                </div>
            </SalesAutomationEditorSidebar>
        );
    }
}

class ClosedLeadStartTriggerSidebarConnected extends React.PureComponent<OwnProps> {
    render() {
        if (this.props.entityId) {
            return (
                <DripSequenceQuery dripSequenceId={this.props.entityId}>
                    {({dripSequence, isLoading}) => (
                        <ClosedLeadStartTriggerSidebarComponent
                            entityId={this.props.entityId}
                            dripSequence={dripSequence ? dripSequence : undefined}
                            isLoading={isLoading}
                            onLinkClick={this.props.onLinkClick}
                        />
                    )}
                </DripSequenceQuery>
            );
        }

        return (
            <ClosedLeadStartTriggerSidebarComponent
                entityId={this.props.entityId}
                // @ts-expect-error
                startTrigger={undefined}
                isLoading={false}
                onLinkClick={this.props.onLinkClick}
            />
        );
    }
}

// @ts-expect-error
const connector = connect<_, OwnProps, _, _, _, _>(undefined, mapDispatchToProps);
export const ClosedLeadStartTriggerSidebar = connector(ClosedLeadStartTriggerSidebarConnected);

function getHeader(status: string) {
    let verb = 'Sending a drip sequence';

    switch (status) {
        case 'PAUSED':
            verb = 'Paused drip sequence';
            break;
        case 'RETIRED':
            verb = 'Retiring drip sequence';
            break;
    }

    return verb;
}

function getVerb(status: string) {
    let verb = 'Sending';

    switch (status) {
        case 'PAUSED':
            verb = 'Paused';
            break;
        case 'RETIRED':
            verb = 'Retiring';
            break;
    }

    return verb;
}

function getDescription(status: string) {
    let description =
        'This drip sequence will automatically begin sending when a lead enters this stage of the pipeline.';

    switch (status) {
        case 'PAUSED':
            description =
                'Resume this drip sequence to automatically begin sending when a lead enters this stage of the pipeline.';
            break;
        case 'RETIRED':
            description =
                'No new recipients will be added to this drip sequence. Once retired, it will no longer appear in this pipeline.';
            break;
    }

    return description;
}
